import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import {
  CreateSessionRequest,
  SessionResponseDto,
  UpdateEventRequest,
  PaymentSelectionRequest,
  PendingPaymentRequest,
  PendingPaymentResponse,
} from 'src/app/model/tracking-api.models';

/**
 * Servicio HTTP para la API REST de tracking
 * Reemplaza las llamadas GraphQL con endpoints REST
 */
@Injectable({
  providedIn: 'root',
})
export class TrackingRestService {
  private apiBaseUrl: string;

  constructor(private http: HttpClient) {
    this.apiBaseUrl = `${environment.endpoints.tracking}/api/tracking`;
  }

  /**
   * Crear una nueva sesión de tracking
   * @param request Datos para crear la sesión
   * @returns Observable con la respuesta de sesión
   */
  async createSession(request: CreateSessionRequest): Promise<SessionResponseDto> {
    try {
      const response = await this.http.post<SessionResponseDto>(`${this.apiBaseUrl}/session`, request).toPromise();

      if (!response) {
        throw new Error('No response from server');
      }

      return response;
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

  /**
   * Actualizar evento de navegación en la sesión
   * @param uid UID único de la sesión
   * @param request Datos del evento
   * @returns Promesa que se resuelve cuando se completa
   */
  async updateSessionEvent(uid: string, request: UpdateEventRequest): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.http.put<{ success: boolean; message: string }>(`${this.apiBaseUrl}/session/${uid}/event`, request).toPromise();

      if (!response) {
        throw new Error('No response from server');
      }

      return response;
    } catch (error) {
      console.error('Error updating session event:', error);
      throw error;
    }
  }

  /**
   * Actualizar selección de método de pago
   * @param uid UID único de la sesión
   * @param request Datos de selección de pago
   * @returns Promesa que se resuelve cuando se completa
   */
  async updatePaymentSelection(uid: string, request: PaymentSelectionRequest): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.http.put<{ success: boolean; message: string }>(`${this.apiBaseUrl}/session/${uid}/payment`, request).toPromise();

      if (!response) {
        throw new Error('No response from server');
      }

      return response;
    } catch (error) {
      console.error('Error updating payment selection:', error);
      throw error;
    }
  }

  /**
   * Actualizar sesión con datos parciales (endpoint genérico)
   * @param uid UID único de la sesión
   * @param request Datos a actualizar (uid, cardNumber, lastStepReached obligatorios + opcionales)
   * @returns Promesa que se resuelve cuando se completa
   */
  async updateSession(uid: string, request: Record<string, unknown>): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.http.put<{ success: boolean; message: string }>(`${this.apiBaseUrl}/session/${uid}`, request).toPromise();

      if (!response) {
        throw new Error('No response from server');
      }

      return response;
    } catch (error) {
      console.error('Error updating session:', error);
      throw error;
    }
  }

  /**
   * Marcar pago como pendiente de confirmación
   * @param uid UID único de la sesión
   * @param request Datos de pago pendiente
   * @returns Promesa con respuesta de pago pendiente
   */
  async markPaymentPending(uid: string, request: PendingPaymentRequest): Promise<PendingPaymentResponse> {
    try {
      const response = await this.http.put<PendingPaymentResponse>(`${this.apiBaseUrl}/session/${uid}/pending`, request).toPromise();

      if (!response) {
        throw new Error('No response from server');
      }

      return response;
    } catch (error) {
      console.error('Error marking payment as pending:', error);
      throw error;
    }
  }

  /**
   * Finalizar sesión con estado COMPLETED o ABANDONED
   * @param uid UID único de la sesión
   * @param status Estado final: COMPLETED o ABANDONED
   * @returns Promesa que se resuelve cuando se completa
   */
  async finalizeSession(uid: string, status: 'COMPLETED' | 'ABANDONED'): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.http.put<{ success: boolean; message: string }>(`${this.apiBaseUrl}/session/${uid}/finalize`, { status }).toPromise();

      if (!response) {
        throw new Error('No response from server');
      }

      return response;
    } catch (error) {
      console.error('Error finalizing session:', error);
      throw error;
    }
  }
}
